import * as Icons from '@lucide/svelte';
import type { FullAutoFill, HTMLInputTypeAttribute } from 'svelte/elements';
import type { IconType } from '../icons/icons.types';
import type { PersonBadgeData, TeamBadgeData, UserBadgeData } from '../values/values.types';

/** The kinds of FormInput; a new one also needs its entry in inputTypeSettings. */
export const allowedInputTypes = ['input', 'email', 'password', 'code', 'color', 'number', 'date', 'search'] as const;
export type AllowedInputType = (typeof allowedInputTypes)[number];

export interface InputTypeSettings {
    /** The `type` attribute of the `<input>`. */
    htmlType: HTMLInputTypeAttribute;
    /** Used when the caller sets no `autocomplete`. */
    autocomplete: FullAutoFill;
    /** The `inputmode` hint for virtual keyboards, if any. */
    inputmode?: 'text' | 'email' | 'numeric' | 'decimal' | 'tel' | 'search' | 'url';
    /** Whether the browser may check the spelling. */
    spellcheck: boolean;
    /** Shown light at the end of the field, telling its kind (FormInput `icon` overrides it). */
    icon?: IconType;
}

/** `input` is a plain single-line text field. */
export const inputTypeSettings: Record<AllowedInputType, InputTypeSettings> = {
    input: { htmlType: 'text', autocomplete: 'off', spellcheck: true },
    email: { htmlType: 'email', autocomplete: 'email', inputmode: 'email', spellcheck: false, icon: Icons.AtSign },
    password: { htmlType: 'password', autocomplete: 'current-password', spellcheck: false, icon: Icons.KeyRound },
    /** A code from a mail or an app — digits, offered by the browser from the message. */
    code: { htmlType: 'text', autocomplete: 'one-time-code', inputmode: 'numeric', spellcheck: false, icon: Icons.ShieldCheck },
    /** The browser's colour picker, `#rrggbb`. */
    color: { htmlType: 'color', autocomplete: 'off', spellcheck: false },
    /** A whole number (a page) — `min` / `max` as attributes. */
    number: { htmlType: 'number', autocomplete: 'off', inputmode: 'numeric', spellcheck: false, icon: Icons.Hash },
    /** A date, `Y-m-d` as value; the browser's picker. */
    date: { htmlType: 'date', autocomplete: 'off', spellcheck: false, icon: Icons.Calendar },
    search: { htmlType: 'search', autocomplete: 'off', inputmode: 'search', spellcheck: false },
};

/** One address of a FormEmails list — every owner of addresses (persons, organizations, …) uses that component. */
export interface FormEmail {
    email: string;
    is_primary: boolean;
}

/**
 * One choice of a FormSelect. `label` is always the plain text (screen readers, search); the list shows the richest
 * form given: `user` as UserBadge (acronym in the user's colour), `team` as TeamBadge, `person` as PersonBadge (titles
 * small and italic, family name bold), else `icon` + `label`. `description` adds a small second line.
 */
export interface FormOption {
    value: number | string;
    /** German. */
    label: string;
    description?: string;
    icon?: IconType;
    user?: UserBadgeData;
    team?: TeamBadgeData;
    person?: PersonBadgeData;
    disabled?: boolean;
}

/**
 * One row of `FormSortList`; `badge` = a value in front of the label (the stored sort number), `hint` = small behind it,
 * `color` = the label in it, the row tinted with it.
 */
export interface SortItem {
    id: number;
    label: string;
    badge?: number | string;
    hint?: string;
    color?: string | null;
}

/** One entry of a FormContacts list: the other side (a person or an organization), its position, its own address. */
export interface FormContact {
    [key: string]: boolean | number | string | undefined;
    /** False: the address of this contact stays. */
    can_write?: boolean;
    /** False: this contact stays (taking it out or pointing it elsewhere removes it). */
    can_delete?: boolean;
    other_id: number;
    position_id: number;
    /** Empty = none. */
    email: string;
}
